// Shared recovery logic for stale Vite chunks after a deploy.
//
// Vite content-hashes chunk filenames per build, so a tab left open across a
// deploy still references chunk URLs that no longer exist on the server.
// Instead of surfacing that as a crash, we reload the tab once to pick up
// the fresh index.html (which points at the current chunks).
//
// Used by src/main.tsx (vite:preloadError) and
// src/components/ErrorBoundary.tsx (render-time dynamic import failures) so
// both paths share one 30s reload guard and avoid looping forever if the
// stale-chunk error keeps recurring (e.g. genuinely broken deploy).

const STORAGE_KEY = "mc:chunk-reload";
const GUARD_WINDOW_MS = 30_000;

export const CHUNK_LOAD_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk .* failed/i;

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return CHUNK_LOAD_ERROR_PATTERN.test(message);
}

/**
 * Reloads the page once to recover from a stale chunk, unless a reload
 * already happened within the last 30 seconds (which would indicate the
 * reload didn't fix anything and we'd otherwise loop forever).
 *
 * Returns true if a reload was triggered, false if the guard blocked it.
 */
export function attemptChunkReload(): boolean {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(STORAGE_KEY) ?? "0");
  } catch {
    // sessionStorage unavailable (e.g. locked-down private browsing) — be
    // conservative and skip the reload rather than risk looping.
    return false;
  }

  if (Date.now() - last < GUARD_WINDOW_MS) {
    return false;
  }

  try {
    sessionStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch {
    return false;
  }

  window.location.reload();
  return true;
}
